import { CheckIcon } from "app/components/Icons";
import ResetIcon from "app/components/Icons/ResetIcon";
import IconText from "app/components/IconText";
import { isEqual } from "lodash";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Filter, FilterPickerType } from "../../types";
import SelectedFilter from "./SelectedFilter";
import { initialFiltersValues } from "../Settings/redux/initialState";
import {
  selectBoardFilters,
  selectDraftBoardFilters,
  selectDraftBoardForm,
} from "../Settings/redux/selector";
import { actions as boardSettingsActions } from "../Settings/redux/slice";
import TicketsFilter from "./TicketsFilter";
import SearchButton from "./../SearchButton";
import {
  buildSelectedOptionsMap,
  getAppliedFilterIds,
} from "./filterReduxHelpers";
import Sorting from "./Sorting";
import { enrichBoardFilters, useBoardFilters } from "./useBoardFilters";

const FilterBar = () => {
  const dispatch = useDispatch();
  const draftBoardForm = useSelector(selectDraftBoardForm);
  const draftFilters = useSelector(selectDraftBoardFilters);
  const savedFilters = useSelector(selectBoardFilters);
  const boardFilters = useBoardFilters();

  const filters = draftFilters ?? initialFiltersValues;
  const searchValue = filters.search || "";
  const appliedFilterIds = getAppliedFilterIds(filters);

  const pickerTypeById = useMemo(
    () =>
      Object.fromEntries(
        boardFilters.map((filter) => [filter.id, filter.pickerType]),
      ) as Record<string, FilterPickerType>,
    [boardFilters],
  );

  const selectedOptions = useMemo(
    () =>
      buildSelectedOptionsMap(
        appliedFilterIds,
        filters.filters,
        (filterId) =>
          pickerTypeById[filterId] ?? FilterPickerType.CHECKBOX,
      ),
    [appliedFilterIds, filters.filters, pickerTypeById],
  );

  const filtersToDisplay = useMemo(() => {
    const appliedFilters = appliedFilterIds
      .map((filterId) => boardFilters.find((filter) => filter.id === filterId))
      .filter((filter): filter is Filter => Boolean(filter));

    return enrichBoardFilters(appliedFilters, boardFilters);
  }, [appliedFilterIds, boardFilters]);

  const buttonsVisible = useMemo(
    () => !isEqual(filters, savedFilters ?? initialFiltersValues),
    [filters, savedFilters],
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      boardSettingsActions.setDraftBoardFormData({
        ...draftBoardForm,
        filters: {
          ...filters,
          search: e.target.value,
        },
      }),
    );
  };

  const onDeleteFilter = (filter: { id: string }) => {
    dispatch(boardSettingsActions.removeBoardAppliedFilter(filter.id));
  };

  const handleReset = () => {
    dispatch(boardSettingsActions.resetFilters());
  };

  const handleSave = () => {
    if (!draftBoardForm._id) return;

    dispatch(
      boardSettingsActions.saveBoardFilters({
        id: draftBoardForm._id,
      }),
    );
  };

  const handleAddFilter = (filter: Filter) => {
    dispatch(boardSettingsActions.addBoardAppliedFilter(filter.id));
  };

  const handleSelectOption = (
    filterId: string,
    value: Parameters<typeof boardSettingsActions.updateBoardFilterValue>[0]["value"],
    pickerType: FilterPickerType,
  ) => {
    dispatch(
      boardSettingsActions.updateBoardFilterValue({
        filterId,
        value,
        pickerType,
      }),
    );
  };

  return (
    <div className="flex flex-row gap-[10px] justify-between px-[24px]">
      <div className="flex flex-row gap-[10px]">
        <SearchButton
          placeholder="Search tickets"
          value={searchValue}
          onChange={handleSearchChange}
        />

        <Sorting />

        <div className="border border-[#E9ECF2] h-[32px] w-[0.5px]" />
        <div className="flex flex-row items-center gap-[10px] flex-wrap">
          <SelectedFilter
            options={filtersToDisplay}
            selectedOptions={selectedOptions}
            onSelectOption={(filterId, selectedOption) => {
              const filter = filtersToDisplay.find((item) => item.id === filterId);
              handleSelectOption(
                filterId,
                selectedOption,
                filter?.pickerType ?? FilterPickerType.CHECKBOX,
              );
            }}
            onDeleteFilter={onDeleteFilter}
          />
          <TicketsFilter
            options={boardFilters}
            appliedFilterIds={appliedFilterIds}
            onAddFilter={handleAddFilter}
          />
        </div>
      </div>

      {buttonsVisible && (
        <div className="flex flex-row gap-[10px]">
          <button
            className="text-sm font-normal text-secondary h-[32px] flex flex-row items-center gap-[6px] justify-center px-[10px]"
            onClick={handleReset}
          >
            <IconText
              icon={<ResetIcon size={20} color="#79808A" />}
              text="Reset"
              className="text-sm font-medium mt-[2px] text-secondary"
            />
          </button>
          <button
            className="text-sm font-normal text-secondary h-[32px] flex flex-row items-center gap-[6px] justify-center bg-primary100 text-primary border border-primary rounded-[4px] px-[10px]"
            onClick={handleSave}
          >
            <IconText
              icon={<CheckIcon size={20} color="#5A8DF1" />}
              text="Save"
              className="text-sm font-medium mt-[2px] text-primary"
            />
          </button>
        </div>
      )}
    </div>
  );
};

export default FilterBar;
